import i18n from '@/utils/i18n';
import NoteIcon from '@assets/icons/note.svg';
import React, { useMemo, useState } from 'react';
import SendNoteIcon from '@assets/icons/send-note.svg';
import TrashIcon from '@assets/icons/trash-small-thin.svg';
import { Button } from '@/components/button';
import { Checkbox } from '@/components/checkbox';
import { createStyles } from '@/contexts/theme-context';
import { Divider } from '@usepepper/ui/atoms/divider';
import { FONTS, SCREEN_PADDING } from '@/styles';
import { ItemsContext_OrderGuideItemFieldsFragment } from '@/types';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { MultiStyleText } from '@/components/multi-style-text';
import { NOTES_MAX_LENGTH } from './constants';
import { OrderGuideCollection } from '@/contexts/item-context-types';
import { SvgProps } from 'react-native-svg';
import { TextArea } from '@/components/text-area';
import { TouchableOpacity, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useCanWriteLineItemRequest } from '@/hooks/dynamic-config/use-can-write-line-item-request';
import { useDiscardChangesOnClose } from '@/hooks/use-discard-changes-on-close';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useToasts } from '@/contexts/toast-context';

export interface EditNoteFormValuesV2 {
  memoDeleteAfterNextOrder: boolean;
  memoValue: string;
  requestDeleteAfterNextOrder: boolean;
  requestValue: string;
}

interface Props {
  collections: OrderGuideCollection[];
  onClose: () => void;
  onRegisterCloseHandler?: (handler: () => void) => void;
  onSave: (values: EditNoteFormValuesV2) => Promise<void>;
  orderGuideItem: ItemsContext_OrderGuideItemFieldsFragment;
}

interface NoteContentBaseProps {
  description: string;
  HeaderIcon: React.FC<SvgProps>;
  hidden?: boolean;
  label: string;
  maxLength?: number;
  onChangeText: (text: string) => void;
  onRemove: () => void;
  placeholder: string;
  readOnly?: boolean;
  value: string;
}

interface NoteContentWithDeleteProps extends NoteContentBaseProps {
  checkboxTrackingData: {
    name: string;
    properties: { variantPackUUID?: string };
  };
  deleteAfterNextOrder: boolean;
  deleteLabel: string;
  onDeleteAfterNextOrderChange: () => void;
}

interface NoteContentWithoutDeleteProps extends NoteContentBaseProps {
  checkboxTrackingData?: never;
  deleteAfterNextOrder?: never;
  deleteLabel?: never;
  onDeleteAfterNextOrderChange?: never;
}

type NoteContentProps =
  | NoteContentWithDeleteProps
  | NoteContentWithoutDeleteProps;

export const NoteContent: React.FC<NoteContentProps> = ({
  checkboxTrackingData,
  deleteAfterNextOrder,
  deleteLabel,
  description,
  HeaderIcon,
  hidden = false,
  label,
  onChangeText,
  onDeleteAfterNextOrderChange,
  onRemove,
  placeholder,
  readOnly = false,
  value,
  maxLength = 1000,
}) => {
  const [styles, theme] = useStyles();
  const { isSmallLayout } = useLayoutContext();

  if (hidden) {
    return null;
  }

  // prop types ensure if deleteLabel is provided, the other props are also provided
  const showDeleteCheckbox = deleteLabel !== undefined;

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleRow}>
          <HeaderIcon height={16} width={16} />
          <Typography variant="paragraph-base-600">{label}</Typography>
        </View>
        {!readOnly && (
          <TouchableOpacity
            accessibilityLabel={i18n.t('remove')}
            accessibilityRole="button"
            hitSlop={{ bottom: 8, left: 8, right: 8, top: 8 }}
            onPress={onRemove}
          >
            <TrashIcon
              height={24}
              stroke={theme.sk.color.icon.primary}
              width={24}
            />
          </TouchableOpacity>
        )}
      </View>
      <View style={styles.descriptionContainer}>
        <MultiStyleText
          defaultStyle={styles.descriptionText}
          emphasisStyle={FONTS.primarySemiBold}
        >
          {description}
        </MultiStyleText>
      </View>
      <TextArea
        caption={`${value.length}/${maxLength}`}
        captionStyle={[
          styles.captionText,
          value.length === maxLength ? styles.captionTextError : null,
        ]}
        containerStyle={styles.textAreaContainer}
        editable={!readOnly}
        multiline
        numberOfLines={isSmallLayout ? 4 : 6}
        onChangeText={(text) => onChangeText(text.slice(0, maxLength))}
        placeholder={placeholder}
        selectTextOnFocus={!readOnly}
        value={value}
      />
      {showDeleteCheckbox ? (
        <Checkbox
          disabled={readOnly}
          isSelected={deleteAfterNextOrder}
          label={deleteLabel}
          labelStyle={FONTS.primary}
          onSelect={onDeleteAfterNextOrderChange}
          style={styles.checkbox}
          trackingData={checkboxTrackingData}
        />
      ) : null}
    </View>
  );
};

export const EditNoteContentV2: React.FC<Props> = ({
  collections,
  onClose,
  onRegisterCloseHandler,
  onSave,
  orderGuideItem,
}) => {
  const { bottom } = useSafeAreaInsets();
  const canWriteRequest = useCanWriteLineItemRequest();
  const [styles] = useStyles();
  const { showErrorToast } = useToasts();
  const { isSmallLayout } = useLayoutContext();

  const noteMaxLength =
    useSupplierDynamicConfigValue('line_item_notes_config')?.max_length ??
    NOTES_MAX_LENGTH;

  const initialMemoValue = orderGuideItem.order_guide_restaurant?.memo ?? '';
  const initialRequestValue =
    orderGuideItem.order_guide_restaurant?.request ?? '';

  const [memoValue, setMemoValue] = useState(initialMemoValue);
  const [requestValue, setRequestValue] = useState(initialRequestValue);
  const [memoDeleteAfterNextOrder, setMemoDeleteAfterNextOrder] = useState(
    !orderGuideItem.order_guide_restaurant?.persist_memo,
  );
  const [requestDeleteAfterNextOrder, setRequestDeleteAfterNextOrder] =
    useState(!orderGuideItem.order_guide_restaurant?.persist_request);
  const [isSaving, setIsSaving] = useState(false);

  // Dynamically insert (Collection Name) - (Partition Name) into memo description
  // note: partition name is __not__ shown if the collection has only one partition
  const getMemoDescription = () => {
    const partitionUUID =
      orderGuideItem.order_guide_restaurant
        ?.order_guide_restaurant_partition_uuid;

    for (const collection of collections) {
      const partition = collection.partitions.find(
        (p) => p.uuid === partitionUUID,
      );
      if (partition) {
        if (collection.partitions.length > 1) {
          return i18n.t(
            'order_guide_note.memo_tab_description_multi_partition',
            {
              collectionName: collection.name,
              partitionName: partition.name,
            },
          );
        }
        return i18n.t(
          'order_guide_note.memo_tab_description_single_partition',
          { orderGuideName: collection.name },
        );
      }
    }

    // fallback, this shouldn't ever happen though (would mean item doesn't belong to any collection)
    return i18n.t('order_guide_note.memo_tab_description');
  };

  const sectionCopy = {
    memo: {
      deleteLabel: i18n.t('order_guide_note.delete_memo_after_next_order'),
      description: getMemoDescription(),
      label: i18n.t('order_guide_note.memo_tab_label'),
      placeholder: i18n.t('order_guide_note.memo_placeholder'),
    },
    request: {
      deleteLabel: i18n.t('order_guide_note.delete_request_after_next_order'),
      description: i18n.t('order_guide_note.request_tab_description'),
      label: i18n.t('order_guide_note.request_tab_label'),
      placeholder: i18n.t('order_guide_note.request_placeholder'),
    },
  };

  const hasUnsavedChanges = useMemo(() => {
    const initialMemoDeleteAfterNextOrder =
      !orderGuideItem.order_guide_restaurant?.persist_memo;
    const initialRequestDeleteAfterNextOrder =
      !orderGuideItem.order_guide_restaurant?.persist_request;

    return (
      memoValue !== initialMemoValue ||
      requestValue !== initialRequestValue ||
      memoDeleteAfterNextOrder !== initialMemoDeleteAfterNextOrder ||
      requestDeleteAfterNextOrder !== initialRequestDeleteAfterNextOrder
    );
  }, [
    initialMemoValue,
    initialRequestValue,
    memoDeleteAfterNextOrder,
    memoValue,
    orderGuideItem.order_guide_restaurant?.persist_memo,
    orderGuideItem.order_guide_restaurant?.persist_request,
    requestDeleteAfterNextOrder,
    requestValue,
  ]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await onSave({
        memoDeleteAfterNextOrder,
        memoValue,
        requestDeleteAfterNextOrder,
        requestValue,
      });
      onClose();
    } catch (error) {
      showErrorToast();
      setIsSaving(false);
    }
  };

  const { discardConfirmationSheet } = useDiscardChangesOnClose({
    hasUnsavedChanges,
    isSaving,
    onClose,
    onRegisterCloseHandler,
    save: handleSave,
  });

  const checkboxTrackingBase = {
    name: 'edit_note_action_sheet_v2.delete_notes_checkbox',
    properties: { variantPackUUID: orderGuideItem.variant_pack?.uuid },
  };

  return (
    <>
      <KeyboardAwareScrollView
        alwaysBounceVertical={false}
        bottomOffset={24}
        contentContainerStyle={[
          !isSmallLayout ? styles.scrollViewContentContainer : {},
          { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
        ]}
        keyboardDismissMode="on-drag"
      >
        <View style={styles.noteContentContainer}>
          <NoteContent
            checkboxTrackingData={checkboxTrackingBase}
            deleteAfterNextOrder={memoDeleteAfterNextOrder}
            deleteLabel={sectionCopy.memo.deleteLabel}
            description={sectionCopy.memo.description}
            HeaderIcon={NoteIcon}
            label={sectionCopy.memo.label}
            maxLength={noteMaxLength}
            onChangeText={setMemoValue}
            onDeleteAfterNextOrderChange={() =>
              setMemoDeleteAfterNextOrder(!memoDeleteAfterNextOrder)
            }
            onRemove={() => {
              setMemoValue('');
            }}
            placeholder={sectionCopy.memo.placeholder}
            value={memoValue}
          />

          {isSmallLayout && canWriteRequest ? (
            <Divider style={styles.divider} />
          ) : null}

          <NoteContent
            checkboxTrackingData={checkboxTrackingBase}
            deleteAfterNextOrder={requestDeleteAfterNextOrder}
            deleteLabel={sectionCopy.request.deleteLabel}
            description={sectionCopy.request.description}
            HeaderIcon={SendNoteIcon}
            hidden={!canWriteRequest}
            label={sectionCopy.request.label}
            maxLength={noteMaxLength}
            onChangeText={setRequestValue}
            onDeleteAfterNextOrderChange={() =>
              setRequestDeleteAfterNextOrder(!requestDeleteAfterNextOrder)
            }
            onRemove={() => {
              setRequestValue('');
            }}
            placeholder={sectionCopy.request.placeholder}
            value={requestValue}
          />
        </View>
        <Button
          loading={isSaving}
          onPress={handleSave}
          trackingData={{
            name: 'edit_note_action_sheet_v2.save_button',
            properties: {
              variantPackUUID: orderGuideItem.variant_pack?.uuid,
            },
          }}
        >
          {i18n.t('save')}
        </Button>
      </KeyboardAwareScrollView>
      {discardConfirmationSheet}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  captionText: {
    textAlign: 'right',
  },
  captionTextError: {
    color: theme.colors.negative,
  },
  checkbox: {
    marginTop: theme.sk.spacing.spaceSmall,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingTop: 0,
  },
  descriptionContainer: {
    borderRadius: theme.sk.radii.radiusMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  descriptionText: {
    ...FONTS.primary,
    color: theme.sk.color.text.secondary,
    fontSize: theme.sk.typography.paragraph.md.fontSize,
    lineHeight: theme.sk.typography.paragraph.md.lineHeight,
  },
  divider: {
    marginBottom: theme.sk.spacing.spaceLarge,
  },
  noteContentContainer: {
    flex: 1,
  },
  scrollViewContentContainer: {
    flexGrow: 1,
    paddingHorizontal: SCREEN_PADDING,
  },
  section: {
    marginBottom: theme.sk.spacing.spaceLarge,
  },
  sectionHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  sectionTitleRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  textAreaContainer: {
    borderColor: theme.sk.color.border.input.default,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
  },
}));
